import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { extensionsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ExtensionFormPage = () => {
  const { id } = useParams(); // This is now extension_id
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditing);
  const [extensionData, setExtensionData] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      extension: '',
      name: '',
      email: '',
      secret: '',
      voicemail_enabled: true,
      voicemail_password: '',
      call_waiting: true,
      ringtime: 30,
    },
  });

  useEffect(() => {
    if (isEditing) {
      fetchExtension();
    }
  }, [id, isEditing]);

  const fetchExtension = async () => {
    try {
      const response = await extensionsAPI.getById(id);
      const data = response.data?.data || response.data;
      
      // Store full extension data for reference
      setExtensionData(data);
      
      // Map VitalPBX fields to form fields
      reset({
        extension: data.extension || '',
        name: data.name || '',
        email: data.email || '',
        // Voicemail is enabled if mailbox field exists and is not empty
        voicemail_enabled: Boolean(data.mailbox && data.mailbox.length > 0),
        voicemail_password: '',
        call_waiting: data.call_waiting ?? true,
        ringtime: data.ringtime || 30,
      });
    } catch (error) {
      console.error('Failed to fetch extension:', error);
      toast.error('Failed to load extension');
      navigate('/extensions');
    } finally {
      setIsFetching(false);
    }
  };

  const onSubmit = async (formData) => {
    setIsLoading(true);

    try {
      // Map form fields to VitalPBX API fields
      const apiData = {
        name: formData.name,
        email: formData.email,
        call_waiting: formData.call_waiting,
        ringtime: parseInt(formData.ringtime) || 30,
      };

      // Only include secret for new extensions or if provided
      if (!isEditing && formData.secret) {
        apiData.secret = formData.secret;
      }

      // Only include extension number for new extensions
      if (!isEditing) {
        apiData.extension = formData.extension;
      }

      if (isEditing) {
        await extensionsAPI.update(id, apiData);
        toast.success('Extension updated successfully');
      } else {
        await extensionsAPI.create(apiData);
        toast.success('Extension created successfully');
      }
      navigate('/extensions');
    } catch (error) {
      console.error('Failed to save extension:', error);
      const message = error.response?.data?.error || 'Failed to save extension';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/extensions')}
          className="flex items-center text-slate-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Extensions
        </button>
        <h1 className="text-2xl font-bold text-white">
          {isEditing ? 'Edit Extension' : 'Add Extension'}
        </h1>
        <p className="text-slate-400 mt-1">
          {isEditing ? 'Update extension settings' : 'Create a new phone extension'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-5">
          <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label">Extension Number *</label>
              <input
                type="text"
                {...register('extension', {
                  required: 'Extension number is required',
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Extension must contain only numbers',
                  },
                })}
                disabled={isEditing}
                className={`input ${errors.extension ? 'input-error' : ''} ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                placeholder="1001"
              />
              {errors.extension && (
                <p className="mt-1 text-sm text-red-400">{errors.extension.message}</p>
              )}
            </div>

            <div>
              <label className="label">Display Name *</label>
              <input
                type="text"
                {...register('name', { required: 'Name is required' })}
                className={`input ${errors.name ? 'input-error' : ''}`}
                placeholder="John Smith"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="label">Email Address</label>
            <input
              type="email"
              {...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className={`input ${errors.email ? 'input-error' : ''}`}
              placeholder="john@company.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
            )}
          </div>

          {!isEditing && (
            <div>
              <label className="label">SIP Password *</label>
              <input
                type="password"
                {...register('secret', {
                  required: !isEditing ? 'Password is required' : false,
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
                className={`input ${errors.secret ? 'input-error' : ''}`}
                placeholder="••••••••"
              />
              {errors.secret && (
                <p className="mt-1 text-sm text-red-400">{errors.secret.message}</p>
              )}
              <p className="mt-1 text-xs text-slate-500">
                This is the password for SIP registration
              </p>
            </div>
          )}
        </div>

        <div className="card space-y-5">
          <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
            Voicemail Settings
          </h2>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-white">Enable Voicemail</label>
              <p className="text-xs text-slate-500">Allow callers to leave voicemail messages</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                {...register('voicemail_enabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div>
            <label className="label">Voicemail PIN</label>
            <input
              type="password"
              {...register('voicemail_password', {
                pattern: {
                  value: /^[0-9]*$/,
                  message: 'PIN must contain only numbers',
                },
              })}
              className={`input ${errors.voicemail_password ? 'input-error' : ''}`}
              placeholder="1234"
            />
            {errors.voicemail_password && (
              <p className="mt-1 text-sm text-red-400">{errors.voicemail_password.message}</p>
            )}
          </div>
        </div>

        <div className="card space-y-5">
          <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
            Call Settings
          </h2>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-white">Call Waiting</label>
              <p className="text-xs text-slate-500">Allow receiving calls while on another call</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                {...register('call_waiting')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div>
            <label className="label">Ring Timeout (seconds)</label>
            <input
              type="number"
              {...register('ringtime', {
                min: { value: 5, message: 'Minimum 5 seconds' },
                max: { value: 120, message: 'Maximum 120 seconds' },
              })}
              className={`input ${errors.ringtime ? 'input-error' : ''}`}
              placeholder="30"
            />
            {errors.ringtime && (
              <p className="mt-1 text-sm text-red-400">{errors.ringtime.message}</p>
            )}
            <p className="mt-1 text-xs text-slate-500">
              How long to ring before going to voicemail
            </p>
          </div>
        </div>

        {/* Device Info (Read Only when editing) */}
        {isEditing && extensionData?.devices && extensionData.devices.length > 0 && (
          <div className="card space-y-5">
            <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
              Registered Devices
            </h2>
            <div className="space-y-3">
              {extensionData.devices.map((device, index) => (
                <div key={device.device_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{device.description || device.device_name}</p>
                    <p className="text-sm text-slate-400">
                      {device.technology} • {device.user}
                      {device.mobile_client === 'yes' && ' • Mobile'}
                      {device.vitxi_client === 'yes' && ' • VitXi'}
                    </p>
                  </div>
                  <span className="badge badge-success">Active</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/extensions')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="spinner w-4 h-4" />
                Saving...
              </span>
            ) : isEditing ? (
              'Update Extension'
            ) : (
              'Create Extension'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExtensionFormPage;
